import { ReactNode } from "react";
import { Box, Chip, Divider, Grid, Paper, Stack, Typography, alpha } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FlagIcon from "@mui/icons-material/Flag";
import dayjs from "dayjs";

import { OrdenTrabajo } from "@/shared/interfaces";
import ImagesWithZoom from "@/shared/components/ui/Images/ImagesWithZoom";
import OtrosConceptosVisita from "../otros-conceptos/OtrosConceptosVisita";
import PuntosDeRedVisita from "../otros-conceptos/PuntosDeRedVisita";
import TipoInstalacionSelector from "../tipo-instalacion/TipoInstalacionSelector";
import { etiquetaTipoInstalacion } from "../tipo-instalacion/useTiposInstalacion";
import TiemposVisita from "../tiempos/TiemposVisita";
import ReinstalacionAcVisita from "../reinstalacion-ac/ReinstalacionAcVisita";

/** URLs (no nulas) de un array de fotos {photoUrl,...} guardado en la OT. */
function urlsFrom(arr: any): string[] {
    if (!Array.isArray(arr)) return [];
    return arr.filter((f: any) => f?.photoUrl).map((f: any) => f.photoUrl as string);
}

const ACCENT = "#1565c0";

function fmt(value?: string | null): string {
    return value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "—";
}

export function duracionVisita(inicio?: string | null, fin?: string | null): string {
    if (!inicio || !fin) return "—";
    const mins = dayjs(fin).diff(dayjs(inicio), "minute");
    if (mins < 0) return "—";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function estadoActivacionColor(
    estado?: string
): "success" | "error" | "warning" | "default" {
    switch (estado) {
        case "GESTIONADA":
        case "PRE_RECHAZADA":
        case "FINALIZADO":
            return "success";
        case "RECHAZADA":
        case "CANCELADO":
            return "error";
        case "PENDIENTE_CAMBIO_PUERTO":
            return "warning";
        default:
            return "default";
    }
}

function fotosFinalizacion(ot: OrdenTrabajo): string[] {
    const photos = ot?.urls_fotos_salida?.photosVisita;
    if (!photos) return [];
    return Object.values(photos)
        .filter((f: any) => f?.photoUrl)
        .map((f: any) => f.photoUrl as string);
}

function SeccionTitulo({ title }: { title: string }) {
    return (
        <Typography
            variant="subtitle1"
            fontWeight={800}
            sx={{ color: ACCENT, fontSize: "1.05rem", mb: 1.5, letterSpacing: 0.2 }}
        >
            {title}
        </Typography>
    );
}

function Campo({ label, value }: { label: string; value?: ReactNode }) {
    return (
        <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                {value || value === 0 ? value : "—"}
            </Typography>
        </Grid>
    );
}

/**
 * Tarjeta de una fase de la visita, con un índice cronológico (badge) + título.
 * Agrupa el contenido relacionado con whitespace y jerarquía clara (skill UI/UX).
 */
function FaseCard({
    paso,
    title,
    children,
}: {
    paso: number;
    title: string;
    children: ReactNode;
}) {
    return (
        <Grid item xs={12}>
            <Paper
                variant="outlined"
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 2,
                    borderColor: "divider",
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            bgcolor: ACCENT,
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 800,
                            fontSize: "0.9rem",
                            flexShrink: 0,
                        }}
                    >
                        {paso}
                    </Box>
                    <Typography
                        variant="subtitle1"
                        fontWeight={800}
                        sx={{ color: ACCENT, fontSize: "1.05rem", letterSpacing: 0.2 }}
                    >
                        {title}
                    </Typography>
                </Stack>
                {children}
            </Paper>
        </Grid>
    );
}

/** Sub-bloque dentro de una FaseCard, con su título y separación uniforme. */
function SubSeccion({
    title,
    children,
    divider = false,
}: {
    title?: string;
    children: ReactNode;
    divider?: boolean;
}) {
    return (
        <Box sx={{ mt: divider ? 2.5 : 0 }}>
            {divider && <Divider sx={{ mb: 2.5 }} />}
            {title && (
                <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1.25, color: "text.primary" }}
                >
                    {title}
                </Typography>
            )}
            {children}
        </Box>
    );
}

/** Lista de trazabilidad (antes/después por usuario y fecha), reutilizable. */
function TrazabilidadLista({
    historial,
    resumen,
}: {
    historial: any[];
    resumen: (v: any) => ReactNode;
}) {
    return (
        <Stack spacing={1}>
            {historial
                .slice()
                .reverse()
                .map((h: any, i: number) => (
                    <Box
                        key={i}
                        sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {h?.usuario || "—"} ·{" "}
                            {h?.fecha ? dayjs(h.fecha).format("DD/MM/YYYY HH:mm") : "—"}
                            {h?.origen ? ` · ${h.origen}` : ""}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <b>Antes:</b> {resumen(h?.anterior)}
                        </Typography>
                        <Typography variant="body2">
                            <b>Después:</b> {resumen(h?.nuevo)}
                        </Typography>
                    </Box>
                ))}
        </Stack>
    );
}

/** Un paso (hito) HORIZONTAL de la línea de tiempo. */
function PasoHito({
    icon,
    title,
    date,
    user,
    extra,
    done,
    isFirst,
    isLast,
}: {
    icon: ReactNode;
    title: string;
    date?: string;
    user?: string;
    extra?: ReactNode;
    done: boolean;
    isFirst?: boolean;
    isLast?: boolean;
}) {
    return (
        <Box sx={{ flex: "1 1 140px", minWidth: 130, textAlign: "center", px: 0.5 }}>
            {/* Círculo + conectores horizontales */}
            <Box
                sx={{
                    position: "relative",
                    height: 34,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                }}
            >
                {!isFirst && (
                    <Box
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: "50%",
                            top: "50%",
                            height: 2,
                            bgcolor: alpha(ACCENT, 0.25),
                        }}
                    />
                )}
                {!isLast && (
                    <Box
                        sx={{
                            position: "absolute",
                            left: "50%",
                            right: 0,
                            top: "50%",
                            height: 2,
                            bgcolor: alpha(ACCENT, 0.25),
                        }}
                    />
                )}
                <Box
                    sx={{
                        zIndex: 1,
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: done ? ACCENT : alpha("#000", 0.06),
                        color: done ? "#fff" : "text.disabled",
                    }}
                >
                    {icon}
                </Box>
            </Box>
            <Typography variant="caption" fontWeight={700} display="block" sx={{ lineHeight: 1.2 }}>
                {title}
            </Typography>
            {date && (
                <Typography variant="caption" color="text.secondary" display="block">
                    {date}
                </Typography>
            )}
            {user && (
                <Typography
                    variant="caption"
                    sx={{ color: ACCENT, fontWeight: 600 }}
                    display="block"
                >
                    por {user}
                </Typography>
            )}
            {extra && <Box sx={{ mt: 0.5 }}>{extra}</Box>}
        </Box>
    );
}

/** Línea de tiempo HORIZONTAL: creación → supervisión → visita → cierre. */
function LineaDeTiempo({ ot }: { ot: OrdenTrabajo }) {
    const ag = (ot as any)?.agendamiento_data || {};
    // Fechas reales del modelo.
    const creada = (ot as any)?.fecha_creacion ?? null;
    const agendada = ag?.fecha_modificacion ?? ag?.fecha_creacion ?? null;
    const inicio = ot?.fecha_hora_inicio_visita;
    const fin = ot?.fecha_hora_fin_visita;
    const requiereCambioOnt = !!ot?.requiere_cambio_ont;
    const activacion = ot?.fecha_ultima_activacion;

    // Usuarios responsables por etapa.
    const userCreacion = (ot as any)?.user_create || undefined;
    const userAgenda = ag?.user_create || undefined;
    const userTecnico =
        (ot as any)?.historial_cambios_coordenadas?.[0]?.usuario || undefined;
    const userActivacion = (ot as any)?.user_activacion || undefined;

    const hitos: Array<{
        icon: ReactNode;
        title: string;
        date?: string;
        user?: string;
        extra?: ReactNode;
        done: boolean;
    }> = [
        {
            icon: <CheckCircleIcon fontSize="small" />,
            title: "Visita creada",
            date: fmt(creada),
            user: userCreacion,
            done: !!creada,
        },
        {
            icon: <EventAvailableIcon fontSize="small" />,
            title: "Agendada / aprobada",
            date: fmt(agendada),
            user: userAgenda,
            done: !!agendada,
        },
        {
            icon: <PlayArrowIcon fontSize="small" />,
            title: "Inicio de visita",
            date: fmt(inicio),
            user: userTecnico,
            done: !!inicio,
        },
    ];

    if (requiereCambioOnt) {
        hitos.push({
            icon: <BoltIcon fontSize="small" />,
            title: "Activación (ONT)",
            date: fmt(activacion),
            user: userActivacion,
            done: !!activacion,
        });
    }

    hitos.push({
        icon: <FlagIcon fontSize="small" />,
        title: "Fin de visita",
        date: fmt(fin),
        user: fin ? userTecnico : undefined,
        done: !!fin,
    });

    hitos.push({
        icon: <FlagIcon fontSize="small" />,
        title: "Estado actual",
        done: true,
        extra: (
            <Chip
                size="small"
                label={ot?.estado_orden_trabajo || "—"}
                color="success"
                variant="outlined"
            />
        ),
    });

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start" }}>
            {hitos.map((h, i) => (
                <PasoHito
                    key={i}
                    {...h}
                    isFirst={i === 0}
                    isLast={i === hitos.length - 1}
                />
            ))}
        </Box>
    );
}

/** Historial de gestión (trazabilidad de venta): acciones con usuario y fecha. */
function HistorialGestion({ ot }: { ot: OrdenTrabajo }) {
    const trazas: any[] = Array.isArray((ot as any)?.trazo_venta_data)
        ? (ot as any).trazo_venta_data
        : [];
    if (trazas.length === 0) return null;
    const ordenadas = [...trazas].sort((a, b) =>
        String(a?.fecha_creacion || "").localeCompare(String(b?.fecha_creacion || "")),
    );
    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                Historial de gestión ({ordenadas.length})
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {ordenadas.map((t, i) => (
                    <Box
                        key={i}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 1,
                            fontSize: "0.8rem",
                            py: 0.25,
                            borderBottom: "1px dashed",
                            borderColor: "divider",
                        }}
                    >
                        <Typography variant="body2">{t?.accion || "—"}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t?.user || "—"} · {fmt(t?.fecha_creacion)}
                        </Typography>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}

/**
 * Observaciones por etapa del flujo real de la visita:
 * Agendamiento (Operaciones) → Técnico → Activaciones. NOC no interviene.
 */
function ObservacionesPorEtapa({ ot }: { ot: OrdenTrabajo }) {
    const obs = ot?.observaciones || {};
    const etapas: Array<{ etapa: string; texto?: string }> = [
        {
            etapa: "Agendamiento (Operaciones)",
            texto:
                obs?.observacion_visita ||
                obs?.observacion_llamada ||
                obs?.observacion_cliente,
        },
        {
            etapa: "Técnico",
            texto:
                obs?.observacion_tecnico ||
                obs?.observacion_incidencia ||
                obs?.observacion_rechazo_tecnico,
        },
        {
            etapa: "Activaciones",
            texto: obs?.observacion_activacion,
        },
    ];
    return (
        <Stack spacing={1.25}>
            {etapas.map((e, i) => (
                <Box
                    key={i}
                    sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: alpha(ACCENT, 0.02),
                    }}
                >
                    <Typography variant="caption" fontWeight={700} sx={{ color: ACCENT }}>
                        {e.etapa}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {e.texto || "Sin observación registrada."}
                    </Typography>
                </Box>
            ))}
        </Stack>
    );
}

/** Galería horizontal de fotos con zoom para una etapa de la visita. */
function GaleriaFotos({ titulo, urls }: { titulo: string; urls: string[] }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {titulo} ({urls.length})
            </Typography>
            {urls.length > 0 ? (
                <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 1 }}>
                    {urls.map((u, i) => (
                        <ImagesWithZoom key={i} src={u} alt={`${titulo}-${i}`} />
                    ))}
                </Stack>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Sin fotos en esta etapa.
                </Typography>
            )}
        </Box>
    );
}

/** Dossier de cierre de una visita finalizada (contenido del modal). */
export default function VisitaFinalizadaDetalle({ ot }: { ot: OrdenTrabajo }) {
    const fotos = fotosFinalizacion(ot);
    const fotosIngreso = urlsFrom((ot as any)?.urls_fotos_ingreso);
    const fotosProceso = urlsFrom((ot as any)?.urls_fotos_proceso);
    const obs = ot?.observaciones || {};
    const estadoActivacion = ot?.estado_activacion;
    // Si el tecnico marco que NO requiere cambio de ONT, no hay activacion que
    // gestionar. Solo sobreescribimos el estado cuando sigue en su valor por
    // defecto (EN ESPERA / vacio), para NO ocultar una activacion ya procesada
    // en registros historicos.
    const requiereCambioOnt = !!ot?.requiere_cambio_ont;
    const sinActivacionRequerida =
        !requiereCambioOnt && (!estadoActivacion || estadoActivacion === "EN ESPERA");
    const userActivacion = (ot as any)?.user_activacion as string | undefined;

    return (
        <Grid container spacing={3}>
            {/* Línea de tiempo del ciclo de vida (overview) — se mantiene tal cual */}
            <Grid item xs={12}>
                <SeccionTitulo title="Ciclo de vida de la visita" />
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: alpha(ACCENT, 0.02),
                    }}
                >
                    <LineaDeTiempo ot={ot} />
                    <HistorialGestion ot={ot} />
                </Box>
            </Grid>

            {/* ① Ejecución de la visita: tiempos + observaciones del técnico */}
            <FaseCard paso={1} title="Ejecución de la visita">
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TiemposVisita ot={ot} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
                            Detalle y observaciones
                        </Typography>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Motivo de la visita
                                </Typography>
                                <Typography variant="body2">{obs?.observacion_visita || "—"}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Observación del técnico
                                </Typography>
                                <Typography variant="body2">{obs?.observacion_tecnico || "—"}</Typography>
                            </Grid>
                            <Campo label="Incidencia" value={ot?.incidencia} />
                            <Campo label="Prioridad" value={ot?.prioridad_incidencia} />
                            <Campo
                                label="Cambio de ONT"
                                value={
                                    <Chip
                                        size="small"
                                        label={ot?.requiere_cambio_ont ? "REQUERIDO" : "No requerido"}
                                        color={ot?.requiere_cambio_ont ? "warning" : "default"}
                                        variant="outlined"
                                    />
                                }
                            />
                            {ot?.requiere_cambio_ont && (
                                <Campo label="Motivo cambio ONT" value={ot?.motivo_cambio_ont} />
                            )}
                            {obs?.observacion_incidencia && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        Observación de incidencia
                                    </Typography>
                                    <Typography variant="body2">{obs.observacion_incidencia}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </FaseCard>

            {/* ② Instalación realizada: tipo + equipos/conceptos + puntos de red */}
            <FaseCard paso={2} title="Instalación realizada">
                <SubSeccion title="Tipo de instalación">
                    <TipoInstalacionSelector
                        value={
                            (ot as any)?.agendamiento_data?.observaciones?.tipo_instalacion ||
                            (ot as any)?.observaciones?.tipo_instalacion ||
                            null
                        }
                        readOnly
                        tipos={[]}
                    />
                </SubSeccion>

                <SubSeccion divider>
                    <ReinstalacionAcVisita
                        value={
                            (ot as any)?.agendamiento_data?.observaciones?.reinstalacion_ac ||
                            (ot as any)?.observaciones?.reinstalacion_ac ||
                            null
                        }
                        readOnly
                    />
                </SubSeccion>

                <SubSeccion title="Equipos y conceptos" divider>
                    <OtrosConceptosVisita
                        value={
                            (ot as any)?.agendamiento_data?.observaciones?.otros_conceptos ||
                            (ot as any)?.observaciones?.otros_conceptos ||
                            []
                        }
                        readOnly
                    />
                </SubSeccion>

                <SubSeccion title="Puntos de red" divider>
                    <PuntosDeRedVisita
                        value={
                            (ot as any)?.agendamiento_data?.observaciones?.puntos_red ||
                            (ot as any)?.observaciones?.puntos_red ||
                            []
                        }
                        readOnly
                    />
                </SubSeccion>
            </FaseCard>

            {/* ③ Evidencia fotográfica — en orden cronológico de la visita */}
            <FaseCard paso={3} title="Evidencia fotográfica">
                <GaleriaFotos titulo="Fotos de ingreso" urls={fotosIngreso} />
                <GaleriaFotos titulo="Fotos de proceso" urls={fotosProceso} />
                <GaleriaFotos titulo="Fotos de finalización" urls={fotos} />
            </FaseCard>

            {/* ④ Activaciones y cierre: respuesta de activaciones + observaciones por etapa */}
            <FaseCard paso={4} title="Activaciones y cierre">
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
                            Respuesta de Activaciones
                        </Typography>
                        <Grid container spacing={1.5}>
                            <Campo
                                label="Estado de activación"
                                value={
                                    sinActivacionRequerida ? (
                                        <Chip
                                            size="small"
                                            label="No requiere activación"
                                            color="default"
                                            variant="outlined"
                                        />
                                    ) : (
                                        <Chip
                                            size="small"
                                            label={estadoActivacion || "—"}
                                            color={estadoActivacionColor(estadoActivacion)}
                                            variant={estadoActivacionColor(estadoActivacion) === "default" ? "outlined" : "filled"}
                                        />
                                    )
                                }
                            />
                            <Campo label="Activado por" value={userActivacion} />
                            <Campo label="Última activación" value={fmt(ot?.fecha_ultima_activacion)} />
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Observación de activaciones
                                </Typography>
                                <Typography variant="body2">{obs?.observacion_activacion || "—"}</Typography>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
                            Observaciones por etapa
                        </Typography>
                        <ObservacionesPorEtapa ot={ot} />
                    </Grid>
                </Grid>
            </FaseCard>

            {/* ⑤ Trazabilidad de cambios (auditoría) — al final */}
            {(() => {
                const tipoHist =
                    (ot as any)?.agendamiento_data?.observaciones?.tipo_instalacion_historial ||
                    (ot as any)?.observaciones?.tipo_instalacion_historial ||
                    [];
                const conceptosHist =
                    (ot as any)?.agendamiento_data?.observaciones?.otros_conceptos_historial ||
                    (ot as any)?.observaciones?.otros_conceptos_historial ||
                    [];
                const puntosHist =
                    (ot as any)?.agendamiento_data?.observaciones?.puntos_red_historial ||
                    (ot as any)?.observaciones?.puntos_red_historial ||
                    [];
                const hayTipo = Array.isArray(tipoHist) && tipoHist.length > 0;
                const hayConceptos = Array.isArray(conceptosHist) && conceptosHist.length > 0;
                const hayPuntos = Array.isArray(puntosHist) && puntosHist.length > 0;
                if (!hayTipo && !hayConceptos && !hayPuntos) return null;

                const resumenConceptos = (arr: any[]) =>
                    Array.isArray(arr) && arr.length
                        ? arr.map((c) => `${c?.concepto || "—"} (${c?.tipo_pago || "—"})`).join(", ")
                        : "—";
                const resumenPuntos = (arr: any[]) =>
                    Array.isArray(arr) && arr.length
                        ? arr
                              .map((p) => `${p?.nombre || "Punto"} (${Number(p?.metros) || 0}m → $${(Number(p?.valor_total) || 0).toFixed(2)})`)
                              .join(", ")
                        : "—";

                return (
                    <FaseCard paso={5} title="Trazabilidad de cambios">
                        {hayTipo && (
                            <SubSeccion title="Tipo de instalación">
                                <TrazabilidadLista
                                    historial={tipoHist}
                                    resumen={(v) => etiquetaTipoInstalacion(v)}
                                />
                            </SubSeccion>
                        )}
                        {hayConceptos && (
                            <SubSeccion title="Conceptos" divider={hayTipo}>
                                <TrazabilidadLista historial={conceptosHist} resumen={resumenConceptos} />
                            </SubSeccion>
                        )}
                        {hayPuntos && (
                            <SubSeccion title="Puntos de red" divider={hayTipo || hayConceptos}>
                                <TrazabilidadLista historial={puntosHist} resumen={resumenPuntos} />
                            </SubSeccion>
                        )}
                    </FaseCard>
                );
            })()}
        </Grid>
    );
}
